import { useEffect, useMemo, useRef } from "react";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import RangeSlider from "./RangeSlider";
import { AMENITIES, OFFICE_TYPES, TIMER, filterOfficeTypesToFilterOptions } from "./constants";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";
import { Workspace } from "./interfaces";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import { useNavigate } from "@remix-run/react";

let priceTimer: string | number | NodeJS.Timeout | null | undefined = null;
let deskTimer: string | number | NodeJS.Timeout | null | undefined = null;

export default function Drawer({
  open,
  setOpen,
  callGeoSearch,
  filterData,
  data,
  workspace,
  url,
  maxPrice,
  onApply,
  isCampaignPage,
  areaSummary,
  isPropertyPage = false,
  isHomePage = false,
}: {
  open: boolean;
  setOpen: Function;
  callGeoSearch: Function;
  filterData: any;
  data: any;
  workspace: Workspace;
  url?: string;
  maxPrice?: number;
  onApply?: Function;
  isCampaignPage?: boolean;
  areaSummary?: any;
  isPropertyPage?: boolean;
  isHomePage?: boolean;
}) {
  const { currencySymbol } = useCurrentCurrency();
  const navigate = useNavigate();
  const initialFiltersRef = useRef<string>("");
  const wasOpenRef = useRef<boolean>(false);

  const normalizeFilters = (filters: any) => {
    if (!filters) {
      return {};
    }

    const normalizeArray = (value: any) => {
      if (!Array.isArray(value)) return [];
      return [...value]
        .filter((item) => item !== undefined && item !== null && item !== "")
        .map((item) => String(item))
        .sort();
    };

    const normalizeValue = (value: any) => {
      if (value === undefined || value === null || value === "") {
        return "";
      }
      return String(value);
    };

    return {
      minPrice: normalizeValue(filters.minPrice),
      maxPrice: normalizeValue(filters.maxPrice),
      minPeople: normalizeValue(filters.minPeople),
      maxPeople: normalizeValue(filters.maxPeople),
      officeTypes: filterOfficeTypesToFilterOptions(normalizeArray(filters.officeTypes)),
      amenities: normalizeArray(filters.amenities),
      facilities: normalizeArray(filters.facilities),
      promo: normalizeValue(filters.promo),
      sale: normalizeValue(filters.sale),
      workspaceId: normalizeValue(filters.workspaceId),
      sortBy: normalizeValue(filters.sortBy),
      order: normalizeValue(filters.order),
    };
  };

  const normalizedFiltersString = useMemo(() => {
    return JSON.stringify(normalizeFilters(filterData));
  }, [filterData]);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      initialFiltersRef.current = normalizedFiltersString;
    }
    wasOpenRef.current = open;
  }, [open, normalizedFiltersString]);

  const hasFilterChanges = useMemo(() => {
    if (!open) {
      return false;
    }
    if (!initialFiltersRef.current) {
      return false;
    }
    return initialFiltersRef.current !== normalizedFiltersString;
  }, [open, normalizedFiltersString]);

  const areSearchParamsEqual = (a?: string, b?: string) => {
    if (!a || !b) return false;
    const normalize = (value: string) => {
      const search = value.startsWith("?") ? value.slice(1) : value;
      const params = new URLSearchParams(search);
      const entries = Array.from(params.entries()).sort(
        ([keyA, valueA], [keyB, valueB]) => {
          if (keyA === keyB) {
            return valueA.localeCompare(valueB);
          }
          return keyA.localeCompare(keyB);
        }
      );
      return entries.map(([key, value]) => `${key}=${value}`).join("&");
    };
    return normalize(a) === normalize(b);
  };

  const shouldApplyFilters = () => {
    if (hasFilterChanges) {
      return true;
    }
    if (onApply) {
      return false;
    }
    if (typeof window === "undefined") {
      return false;
    }
    if (!url) {
      return false;
    }
    return !areSearchParamsEqual(window.location.search, url);
  };

  const currentOfficeTypes: string[] = Array.isArray(filterData?.officeTypes)
    ? filterData.officeTypes
    : [];
  const currentAmenities: string[] = Array.isArray(filterData?.amenities)
    ? filterData.amenities
    : [];
  const priceRangeLabel = areaSummary
    ? "Price (per desk / month)"
    : isCampaignPage
      ? "Budget (per desk / month)"
      : "Price (per month)";

  const showAmenitiesAndSale = !areaSummary && !isPropertyPage && !isHomePage;

  return (
    <Sheet open={open} onOpenChange={(val) => setOpen(val)}>
      <SheetContent className={`overflow-y-auto p-0 flex flex-col justify-between ${isCampaignPage ? 'lg:min-w-[450px] md:min-w-[700px] sm:min-w-[600px]' : 'lg:w-[600px] md:w-[600px] sm:w-[500px]'}`}>
        <SheetHeader className="p-5 bg-gray-100">
          <SheetTitle>Filterations</SheetTitle>
        </SheetHeader>
        {/* Desk Range */}
        <div className="overflow-y-auto flex-1 p-5 mt-[-20px]">
          <div className="mb-8">
            <RangeSlider
              defaultMin={0}
              defaultMax={500}
              type="desk"
              initialValue={filterData?.deskRange}
              onChange={({ min, max }) => {
                if (deskTimer) {
                  clearTimeout(deskTimer);
                }
                deskTimer = setTimeout(() => {
                  callGeoSearch({
                    minPeople: min?.toString() || "",
                    maxPeople: max?.toString() || "",
                  });
                }, TIMER.RANGE);
              }}
              label={"Desks (number of people)"}
              prefix={""}
              reset={() =>
                callGeoSearch({ minPeople: "reset", maxPeople: "reset" })
              }
            />
          </div>

          {/* Price Range */}
          <div className="mb-8">
            <RangeSlider
              defaultMin={0}
              defaultMax={maxPrice || 100000}
              type={areaSummary || isPropertyPage ? "Desk Price" : "Price"}
              initialValue={filterData?.price}
              onChange={({ min, max }) => {
                if (priceTimer) {
                  clearTimeout(priceTimer);
                }
                priceTimer = setTimeout(() => {
                  callGeoSearch({
                    minPrice: min?.toString() || "",
                    maxPrice: max?.toString() || "",
                    priceTiers: "reset",
                  });
                }, TIMER.RANGE);
              }}
              label={priceRangeLabel}
              reset={() =>
                callGeoSearch({ minPrice: "reset", maxPrice: "reset", priceTiers: "reset" })
              }
              prefix={currencySymbol}
              inputClassName="px-3 py-[10px] bg-gray-200 text-gray-600 font-normal text-sm select-none"
            />
          </div>

          {(!isCampaignPage && showAmenitiesAndSale) && (
            <div className="mb-8">
              <label className="block text-base font-semibold">SALE</label>
              <Button
                onClick={() => {
                  if (filterData?.promo || filterData?.sale) {
                    callGeoSearch({ promo: "", sale: null });
                  } else {
                    callGeoSearch({ promo: "sale", sale: "sale" });
                  }
                }}
                variant="outline"
                className={`mt-3 px-4 ${filterData?.promo || filterData?.sale ? "bg-oh-secondary text-white" : "bg-white text-black"}`}
              >
                On Sale
              </Button>
            </div>
          )}

          {/* Provider */}
          {filterData?.workspaceId ? (
            <div className="mb-8">
              <label className="block text-base font-semibold">Provider</label>
              <OhButton variant="destructive" className="mt-3">
                {workspace?.name}
              </OhButton>
            </div>
          ) : null}

          {/* Office Type */}
          <div className="mb-8">
            <label className="block text-base font-semibold">Office Type</label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {OFFICE_TYPES.map((o) => {
                const isSelected = currentOfficeTypes.includes(o.value);

                return (
                  <div key={o.value} className="flex items-center gap-2">
                    <Checkbox
                      id={o.value}
                      checked={isSelected}
                      onCheckedChange={() => {
                        const nextTypes = isSelected
                          ? currentOfficeTypes.filter((i) => i !== o.value)
                          : [...currentOfficeTypes, o.value];
                        const allTypeValues = OFFICE_TYPES.map((t) => t.value);
                        const allSelected =
                          nextTypes.length === allTypeValues.length &&
                          allTypeValues.every((v) => nextTypes.includes(v));
                        callGeoSearch({
                          workspaceType: allSelected
                            ? "All"
                            : nextTypes.length > 0
                              ? nextTypes.join(",")
                              : "reset",
                          ...(areaSummary
                            ? { officeTypeFilterTouched: "1" }
                            : {}),
                        });
                      }}
                    />
                    <label htmlFor={o.value} className="text-sm">
                      {o.label}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Amenities */}
          {showAmenitiesAndSale ? <div className="mb-8">
            <label className="block text-base font-semibold">Amenities</label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {AMENITIES.map((amenity) => {
                const isSelected = currentAmenities.includes(amenity.value);
                return (
                  <div key={amenity.value} className="flex items-center gap-2">
                    <Checkbox
                      id={amenity.value}
                      checked={isSelected}
                      onCheckedChange={() => {
                        const nextAmenities = isSelected
                          ? currentAmenities.filter((i) => i !== amenity.value)
                          : [...currentAmenities, amenity.value];
                        callGeoSearch({
                          amenities: nextAmenities.join(","),
                        });
                      }}
                    />
                    <label htmlFor={amenity.value} className="text-sm">
                      {amenity.label}
                    </label>
                  </div>
                );
              })}
            </div>
          </div> : null}
        </div>

        <SheetFooter className="px-5 py-4 bg-gray-100">
          <div className="flex gap-4">
            <Button
              variant="outline"
              type="submit"
              onClick={() => {
                callGeoSearch({ reset: true });
                setOpen(false);
              }}
            >
              Reset
            </Button>
            <SheetClose asChild>
              <OhButton
                onClick={() => {
                  if (!shouldApplyFilters()) {
                    return;
                  }
                  if (onApply) {
                    onApply();
                  } else if (url) {
                    navigate(url);
                  }
                }}
              >
                Show Results
              </OhButton>
            </SheetClose>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  ); 
}